import React, { useEffect, useRef, useState } from 'react';
import { useConfig } from '../../../DataStore/ConfigContext';
import Notification from '../../Common/Notification';
import CallOut from '../../Common/CallOut';
import ajaxConfig from '../../../util/remote/ajaxConfig';
import { useTheme } from '../../../DataStore/ThemeContext';

const MAX_SIZE_MB = 3;
const ALLOWED = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

const UploadLogo = () => {
  const fileRef = useRef(null);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [imgMeta, setImgMeta] = useState(null);
  const [preview, setPreview] = useState('');
  const [payload, setPayload] = useState(null);

  const { getCompanyProfile } = useConfig();
  const { setSystemFunction } = useTheme();

  useEffect(() => {
    setSystemFunction(() => onSave);
    return () => setSystemFunction(() => {});
  }, [payload]);

  const showError = (msg) => setNotify(<Notification type="danger" message={msg} />);
  const showOk = (msg) => setNotify(<Notification type="success" message={msg} />);

  const validateFile = (file) => {
    if (!file) return false;
    if (!ALLOWED.includes(file.type)) {
      showError('Please upload a PNG, JPG, WEBP or SVG image.');
      return false;
    }
    if (file.size / (1024 * 1024) > MAX_SIZE_MB) {
      showError(`File is too large. Max allowed is ${MAX_SIZE_MB} MB.`);
      return false;
    }
    return true;
  };

  const handleChosenFile = async (file) => {
    if (!validateFile(file)) return;
    setFileName(file.name);
    setFileSize(file.size);

    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      setPreview(base64);
      setPayload(base64);
      const img = new Image();
      img.onload = () => setImgMeta({ w: img.naturalWidth, h: img.naturalHeight });
      img.src = base64;
    };
    reader.readAsDataURL(file);
  };

  const onSave = async () => {
    if (!payload) return showError('Please select a logo first.');
    setLoading(true);
    const res = await ajaxConfig.uploadLogo({ logo_file: { file: payload } });
    setLoading(false);
    if (res?.status === 'OK') {
      showOk('Logo updated successfully.');
      getCompanyProfile(true);
    } else {
      showError(res?.message || 'Upload failed.');
    }
  };

  return (
    <div className="upload-container">
      <CallOut
        title="Brand Identity"
        icon="fa-solid fa-wand-magic-sparkles"
        description="Upload a high-resolution logo for your dashboard and documents. For the best results, use a transparent PNG or SVG."
      />
      <div className="mb-4 mt-4 animate__animated animate__fadeIn">
      {notify}
      </div>
     

      <div className="row mt-4">
        {/* Dropzone Area */}
        <div className="col-md-6">
          <div
            className={`dropzone-card ${dragOver ? 'drag-active' : ''} ${preview ? 'has-preview' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleChosenFile(e.dataTransfer.files[0]); }}
          >
            {preview ? (
              <div className="preview-content">
                <div className="preview-frame">
                  <img src={preview} alt="Preview" />
                </div>
                <div className="file-info mt-3">
                  <strong>{fileName}</strong>
                  <span>{(fileSize / 1024).toFixed(0)} KB • {imgMeta?.w}×{imgMeta?.h} px</span>
                </div>
                <button className="btn-remove" onClick={() => {setPreview(''); setPayload(null);}}>
                  <i className="fa-solid fa-trash-can mr-2" /> Remove Logo
                </button>
              </div>
            ) : (
              <div className="upload-placeholder" onClick={() => fileRef.current?.click()}>
                <div className="icon-box">
                  <i className="fa-solid fa-cloud-arrow-up" />
                </div>
                <h5>Drag & drop logo here</h5>
                <p>PNG, JPG, or SVG up to {MAX_SIZE_MB}MB</p>
                <button className="btn-browse">Browse Files</button>
              </div>
            )}
          </div>
        </div>

        {/* Requirements Area */}
        <div className="col-md-6">
          <div className="requirements-panel">
            <h6 className="section-title">Upload Requirements</h6>
            <ul className="modern-list">
              <li>
                <i className="fa-solid fa-check-circle" />
                <span><strong>Ratio:</strong> 1:1 (Square) is recommended for sidebar alignment.</span>
              </li>
              <li>
                <i className="fa-solid fa-check-circle" />
                <span><strong>Format:</strong> Transparent PNG or SVG avoids white boxes on dark themes.</span>
              </li>
              <li>
                <i className="fa-solid fa-check-circle" />
                <span><strong>Size:</strong> Aim for 512 &times; 512 pixels for high-density displays.</span>
              </li>
            </ul>
            
            <input 
              ref={fileRef} 
              type="file" 
              className="d-none" 
              accept={ALLOWED.join(',')} 
              onChange={(e) => handleChosenFile(e.target.files[0])} 
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadLogo;